import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaEye, FaEyeSlash, FaPlus, FaSearch } from 'react-icons/fa';
import { 
  TextField, 
  Button, 
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  InputAdornment
} from '@mui/material';
import { Container, Header, HeaderLeft, Title, Content, BackButton, HeaderButton, ControlsContainer, MainContainer } from '../../common/styles';
import ConfirmationModal from '../../common/ConfirmationModal';
import Modal from '../../common/Modal';
import LoadingSpinner from '../../common/LoadingSpinner';
import { useAlert } from '../../../contexts/AlertContext';
import { organizationApi } from '../../../services/api';

const EmployeeContractTypesConfiguration = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [showControls, setShowControls] = useState(true);
  const [contractTypes, setContractTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  
  // Unified modal state
  const [contractTypeModal, setContractTypeModal] = useState({
    open: false,
    isEdit: false,
    formData: { name: '', code: '' },
    originalId: null
  });
  
  const [deleteModal, setDeleteModal] = useState({ open: false, contractType: null });

  const fetchContractTypes = async () => {
    try {
      setIsLoading(true);
      const data = await organizationApi.getEmployeeContractTypes();
      setContractTypes(data);
    } catch (err) {
      showAlert('Failed to load employee contract types.', 'error', err);
      console.error('Error fetching employee contract types:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchContractTypes();
  }, []);

  // Unified handler for both add and edit
  const handleSaveContractType = async (e) => {
    e.preventDefault();
    
    try {
      setIsLoading(true);
      
      if (contractTypeModal.isEdit) {
        // Edit existing contract type
        if (!contractTypeModal.originalId) {
          showAlert('Invalid employee contract type data for update', 'error');
          return;
        }
        
        await organizationApi.updateEmployeeContractType(contractTypeModal.originalId, {
          name: contractTypeModal.formData.name,
          code: contractTypeModal.formData.code
        });
        showAlert('Individual contract type updated successfully', 'success');
      } else {
        // Add new contract type
        await organizationApi.createEmployeeContractType({
          name: contractTypeModal.formData.name,
          code: contractTypeModal.formData.code
        });
        showAlert('Individual contract type added successfully', 'success');
      }
      
      await fetchContractTypes();
      // Reset modal state
      setContractTypeModal({
        open: false,
        isEdit: false,
        formData: { name: '', code: '' },
        originalId: null
      });
    } catch (err) {
      const action = contractTypeModal.isEdit ? 'update' : 'add';
      showAlert(err.message || `Failed to ${action} individual contract type.`, 'error', err);
      console.error(`Error ${action}ing employee contract type:`, err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteContractType = (contractType) => {
    setDeleteModal({ open: true, contractType });
  };

  const confirmDelete = async () => {
    const typeToDelete = deleteModal.contractType;
    setDeleteModal({ open: false, contractType: null });
    
    if (!typeToDelete?.id) {
      showAlert('Invalid individual contract type data', 'error');
      return;
    }

    try {
      setIsLoading(true);
      await organizationApi.deleteEmployeeContractType(typeToDelete.id);
      await fetchContractTypes();
      showAlert('Individual contract type deleted successfully', 'success');
    } catch (err) {
      console.error('Error deleting employee contract type:', err);
      showAlert(err.message || 'Failed to delete individual contract type.', 'error', err);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredContractTypes = contractTypes.filter(type => {
    if (!searchTerm) return true;
    return type.name.toLowerCase().includes((searchTerm || '').toLowerCase());
  });

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/organization')}>
            <FaArrowLeft /> Back to Organization
          </BackButton>
          <Title>Individual Contract Types Configuration</Title>
        </HeaderLeft>
        <HeaderButton onClick={() => setShowControls(!showControls)}>
          {showControls ? <FaEyeSlash /> : <FaEye />}
          {showControls ? 'Hide Panels' : 'Show Panels'}
        </HeaderButton>
      </Header>

      <Content>
        <div style={{ flex: 1 }}>
          <ControlsContainer $show={showControls} style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between' }}>
            <TextField
              placeholder="Search Individual Contract Type"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch style={{ color: '#6B7280' }} />
                    </InputAdornment>
                  ),
                }
              }}
              sx={{
                minWidth: '300px',
              }}
            />
            <HeaderButton onClick={() => setContractTypeModal({
              open: true,
              isEdit: false,
              formData: { name: '', code: '' },
              originalId: null
            })}>
              <FaPlus /> Add Individual Contract Type
            </HeaderButton>
          </ControlsContainer>

          <MainContainer $show={showControls}>
            <div style={{ 
              width: '100%',
              height: '100%',
              overflowY: 'auto',
              padding: '24px'
            }}>
              <Grid 
                container 
                spacing={3}
                sx={{
                  width: '100%',
                  margin: '0 auto',
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                    lg: 'repeat(4, 1fr)',
                    xl: 'repeat(4, 1fr)'
                  },
                  gap: '24px',
                  '& .MuiGrid-item': {
                    padding: '0',
                    width: '100%'
                  }
                }}
              >
                {filteredContractTypes.map((contractType) => (
                  <Grid item key={contractType.id}>
                    <Card 
                      sx={{ 
                        width: '100%',
                        minHeight: '120px',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.2s',
                        border: '1px solid #e0e0e0',
                        boxShadow: 'none',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        backgroundColor: '#f8f9fa',
                        '&:hover': {
                          borderColor: '#bdbdbd',
                          backgroundColor: '#f1f3f5',
                        }
                      }}
                    >
                      <CardContent sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        padding: '16px !important',
                        flexGrow: 1,
                      }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 500,
                            color: '#212529',
                            marginBottom: '4px'
                          }}
                        >
                          {contractType.name}
                        </Typography>

                        {contractType.code && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#868e96',
                              fontFamily: 'monospace',
                              fontSize: '12px',
                              display: 'block',
                              marginBottom: '8px'
                            }}
                          >
                            Code: {contractType.code}
                          </Typography>
                        )}

                        {contractType.employeeCount > 0 && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#868e96',
                              fontStyle: 'italic'
                            }}
                          >
                            Attached to {contractType.employeeCount} consultant{contractType.employeeCount !== 1 ? 's' : ''}
                          </Typography>
                        )}
                        
                        <Box sx={{ 
                          display: 'flex',
                          justifyContent: 'flex-end',
                          gap: '8px',
                          marginTop: '16px'
                        }}>
                          <Button
                            onClick={() => setContractTypeModal({
                              open: true,
                              isEdit: true,
                              formData: { name: contractType.name, code: contractType.code || '' },
                              originalId: contractType.id
                            })}
                            sx={{
                              minWidth: 'unset',
                              padding: '6px 12px',
                              fontSize: '14px',
                              color: '#0d6efd',
                              transition: 'all 0.2s ease',
                              backgroundColor: 'rgba(13, 110, 253, 0.1)',
                              textTransform: 'none',
                              '&:hover': {
                                backgroundColor: 'rgba(13, 110, 253, 0.15)',
                                color: '#0d6efd'
                              }
                            }}
                          >
                            Edit
                          </Button>

                          <Button
                            onClick={() => handleDeleteContractType(contractType)}
                            sx={{
                              minWidth: 'unset',
                              padding: '6px 12px',
                              fontSize: '14px',
                              color: '#dc3545',
                              transition: 'all 0.2s ease',
                              backgroundColor: 'rgba(220, 53, 69, 0.1)',
                              textTransform: 'none',
                              '&:hover': {
                                backgroundColor: 'rgba(220, 53, 69, 0.15)',
                                color: '#dc3545'
                              }
                            }}
                          >
                            Delete
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </div>
          </MainContainer>
        </div>
      </Content>

      {/* Unified modal for both adding and editing employee contract types */}
      <Modal
        isOpen={contractTypeModal.open}
        onClose={() => setContractTypeModal({
          open: false,
          isEdit: false,
          formData: { name: '', code: '' },
          originalId: null
        })}
        onSubmit={handleSaveContractType}
        title={contractTypeModal.isEdit ? "Edit Individual Contract Type" : "Add New Individual Contract Type"}
        submitText={contractTypeModal.isEdit ? "Update Individual Contract Type" : "Add Individual Contract Type"}
        submitDisabled={!contractTypeModal.formData.name.trim() || !contractTypeModal.formData.code.trim()}
      >
        <Box mb={2}>
          <TextField
            id="employee-contract-type-name"
            name="name"
            label="Individual Contract Type Name"
            required
            value={contractTypeModal.formData.name}
            onChange={(e) => setContractTypeModal(prev => ({
              ...prev,
              formData: { ...prev.formData, name: e.target.value }
            }))}
            placeholder="Enter individual contract type name"
            autoComplete="off"
            fullWidth
            variant="outlined"
          />
        </Box>
        <Box mb={2}>
          <TextField
            id="employee-contract-type-code"
            name="code"
            label="Individual Contract Type Code"
            required
            value={contractTypeModal.formData.code}
            onChange={(e) => setContractTypeModal(prev => ({
              ...prev,
              formData: { ...prev.formData, code: e.target.value }
            }))}
            placeholder="Enter individual contract type code (e.g., FT, PT)"
            autoComplete="off"
            fullWidth
            variant="outlined"
          />
        </Box>
      </Modal>

      <ConfirmationModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, contractType: null })}
        onConfirm={confirmDelete}
        title="Delete Individual Contract Type"
        message={`Are you sure you want to delete ${deleteModal.contractType?.name}?`}
        actionButtonText="Delete"
      />
    </Container>
  );
};

export default EmployeeContractTypesConfiguration; 